import { injectable } from 'tsyringe';
import { BaseApi } from '../BaseApi';
import {
    TipranksAnalystRatingByStockSort,
    TipranksTrendingStocksFilter,
    TipranksTrendingStocksTrendingType,
} from '../constants/lookups';
import {
    Analyst,
    AnalystPortfolioItem,
    AnalystPortfolioItemWithExtraInfo,
    AnalystProfileInfo,
    TiprankOverview,
    TiprankOverviewWithExtraInfo,
    TipranksNewsSentiment,
    TopAnalystsPickData,
    TrendingStocksData,
} from '../types/Tipranks.type';
import { divide, multiply } from '../utils';
import { ITipranksApi } from './interfaces/ITipranksApi';

@injectable()
export class TipranksApi extends BaseApi implements ITipranksApi {
    async getTipranksAnalystRatingsBySymbol(
        symbols: string[],
        count?: number,
        sort?: TipranksAnalystRatingByStockSort,
    ): Promise<Analyst[]> {
        return (
            await this.sdks.middleware.post<Analyst[]>('insight/tipranks/analyst-multi', {
                num: count,
                sort,
                tickers: symbols,
            })
        ).data;
    }

    async getTipranksAssetsOverview(symbols: string[]): Promise<TiprankOverviewWithExtraInfo[]> {
        const response = await this.sdks.middleware.post<TiprankOverview[]>('insight/tipranks/analyst-consensus', {
            include_analysts: false,
            tickers: symbols,
        });
        return response.data.map((tipRank) => {
            const totalRatings = tipRank.buy + tipRank.hold + tipRank.sell;
            const buyPercent = multiply(100, divide(tipRank.buy, totalRatings));
            const holdPercent = multiply(100, divide(tipRank.hold, totalRatings));
            const sellPercent = multiply(100, divide(tipRank.sell, totalRatings));

            return {
                ...tipRank,
                totalRatings,
                buyPercent,
                holdPercent,
                sellPercent,
            };
        });
    }

    async getTipranksAnalystProfile(id: string): Promise<AnalystProfileInfo> {
        return (
            await this.sdks.middleware.get<AnalystProfileInfo>('insight/tipranks/analyst-profile', undefined, {
                params: {
                    id,
                },
            })
        ).data;
    }

    async getTipranksAnalystPortfolio(expertUID: string, count?: number): Promise<AnalystPortfolioItemWithExtraInfo[]> {
        const response = await this.sdks.middleware.post<AnalystPortfolioItem[]>(
            // @ts-ignore: URL not added
            'insight/tipranks/analyst-portfolio',
            {
                expert_uid: expertUID,
                number: count || 100,
            },
        );

        return response.data.map((item) => {
            const quote = item.quote;
            return {
                ...item,
                profit: item.price_target === 0 ? 0 : divide(quote.last_px, item.price_target),
            };
        });
    }

    async getTipranksNewsSentiment(symbol: string, num?: number): Promise<TipranksNewsSentiment> {
        const response = await this.sdks.middleware.get('insight/tipranks/news-sentiment', undefined, {
            params: { ticker: symbol, num },
        });
        return response.data.news_sentiment;
    }

    async getTopAnalystRecommendations(
        count?: number,
        period?: number,
        filter?: TipranksTrendingStocksFilter,
        trendingType?: TipranksTrendingStocksTrendingType,
    ): Promise<TrendingStocksData[]> {
        const { data } = await this.sdks.middleware.post('insight/tipranks/trending-stocks', {
            filter,
            num: count,
            period,
            trending_type: trendingType,
        });
        return data;
    }

    async getLivefeedOfAnalystsRatings(count: number, bookmarkId?: string): Promise<TopAnalystsPickData[]> {
        const { data } = await this.sdks.middleware.post('insight/tipranks/live-feed', {
            num: count,
            bookmark_id: bookmarkId,
        });
        return data;
    }
}
